import { format } from "date-fns";
import { Calendar, Edit2, FileText, Filter, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cloudflare } from "@/integrations/cloudflare/client";
import { type QuoteStatus, StatusBadge } from "./StatusBadge";
import { StatusSelector } from "./StatusSelector";

const DEFAULT_QUOTES_API_BASE_URL =
  import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8787";

interface Quote {
  id: string;
  project_id: string;
  quote_name: string;
  quote_number: string;
  status: QuoteStatus;
  notes?: string;
  total_amount: number;
  scheduled_date?: string | null;
  progress_percentage?: number;
  total_checklists?: number;
  completed_checklists?: number;
  created_at: string;
  updated_at: string;
}

interface ApiQuote {
  id: string | number;
  project_id: string | number;
  quote_name: string;
  quote_number: string;
  status: QuoteStatus;
  notes?: string | null;
  total_amount?: number | string | null;
  scheduled_date?: string | null;
  progress_percentage?: number;
  total_checklists?: number;
  completed_checklists?: number;
  created_at: string;
  updated_at: string;
}

interface QuoteListProps {
  projectId: string;
  onViewQuote?: (quote: Quote) => void;
  onEditQuote?: (quote: Quote) => void;
  onGenerateQuote?: (quote: Quote) => void;
}

export function QuoteList({
  projectId,
  onViewQuote,
  onEditQuote,
  onGenerateQuote,
}: QuoteListProps) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [newQuoteName, setNewQuoteName] = useState("");
  const [newQuoteNotes, setNewQuoteNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<{ id: string; name: string } | null>(null);
  const { toast } = useToast();
  const quoteNameFieldId = useId();
  const quoteNotesFieldId = useId();

  const loadQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${DEFAULT_QUOTES_API_BASE_URL}/api/v1/checklist-quotes?project_id=${projectId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch checklist-based quotes");
      }
      const result = await response.json();

      const transformedQuotes: Quote[] = (result.data ?? []).map((quote: ApiQuote) => {
        const totalAmount =
          typeof quote.total_amount === "number"
            ? quote.total_amount
            : Number.parseFloat(quote.total_amount ?? "0");
        return {
          id: quote.id.toString(),
          project_id: quote.project_id.toString(),
          quote_name: quote.quote_name,
          quote_number: quote.quote_number,
          status: quote.status,
          notes: quote.notes ?? "",
          total_amount: Number.isFinite(totalAmount) ? totalAmount : 0,
          scheduled_date: quote.scheduled_date,
          progress_percentage: quote.progress_percentage ?? 0,
          total_checklists: quote.total_checklists ?? 0,
          completed_checklists: quote.completed_checklists ?? 0,
          created_at: quote.created_at,
          updated_at: quote.updated_at,
        };
      });

      setQuotes(transformedQuotes);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load quotes.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  useEffect(() => {
    loadQuotes().catch(() => {
      // Errors are surfaced via toast inside loadQuotes.
    });
  }, [loadQuotes]);

  const createQuoteFromProject = async () => {
    if (!newQuoteName.trim()) {
      toast({
        title: "Quote Name Required",
        description: "Please enter a name for your quote.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreatingQuote(true);

      const { data, error } = await cloudflare.rpc("create_quote_from_project", {
        p_project_id: projectId,
        p_quote_name: newQuoteName.trim(),
      });

      if (error) {
        throw error;
      }

      if (newQuoteNotes.trim()) {
        const { error: updateError } = await cloudflare
          .from("quotes")
          .update({ notes: newQuoteNotes.trim() })
          .eq("id", data);

        if (updateError) {
          throw updateError;
        }
      }

      await loadQuotes();

      // Reset form
      setNewQuoteName("");
      setNewQuoteNotes("");
      setIsCreating(false);

      toast({
        title: "Quote Created",
        description: `Quote "${newQuoteName}" has been created from project data.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create quote.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const handleDeleteClick = (quoteId: string, quoteName: string) => {
    setQuoteToDelete({ id: quoteId, name: quoteName });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!quoteToDelete) {
      return;
    }

    try {
      const response = await fetch(
        `${DEFAULT_QUOTES_API_BASE_URL}/api/v1/checklist-quotes/${quoteToDelete.id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete quote");
      }

      await loadQuotes();
      toast({
        title: "Quote Deleted",
        description: `"${quoteToDelete.name}" has been deleted successfully.`,
      });

      setDeleteModalOpen(false);
      setQuoteToDelete(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete quote.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const updateQuoteStatus = async (
    quoteId: string,
    status: QuoteStatus,
    scheduledDate?: string | null
  ) => {
    try {
      const response = await fetch(
        `${DEFAULT_QUOTES_API_BASE_URL}/api/v1/checklist-quotes/${quoteId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status, scheduled_date: scheduledDate }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update quote status");
      }

      // Also update the parent project's status to match the quote status
      try {
        const projectResponse = await fetch(
          `${DEFAULT_QUOTES_API_BASE_URL}/api/v1/projects/${projectId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
          }
        );

        if (!projectResponse.ok) {
          console.warn(
            "Failed to update project status, but quote status was updated successfully"
          );
        }
      } catch (projectError) {
        console.warn("Error updating project status:", projectError);
        // Don't fail the whole operation if project update fails
      }

      await loadQuotes();
      toast({
        title: "Status Updated",
        description: `Quote status has been updated to ${status}.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update status.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      throw error;
    }
  };

  // Filter quotes based on search term and status
  const filteredQuotes = quotes.filter((quote) => {
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch =
      normalizedSearch.length === 0 ||
      quote.quote_name?.toLowerCase().includes(normalizedSearch) ||
      quote.quote_number?.toLowerCase().includes(normalizedSearch);

    const matchesStatus = statusFilter === "all" || quote.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const openQuoteForEditing = (quote: Quote) => {
    if (onEditQuote) {
      onEditQuote(quote);
      return;
    }
    navigate(`/quote-builder/${projectId}?quoteId=${quote.id}`);
  };

  const openQuoteForViewing = (quote: Quote) => {
    if (onViewQuote) {
      onViewQuote(quote);
      return;
    }
    openQuoteForEditing(quote);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading quotes...</p>
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5 text-primary" />
              Project Quotes
              <Badge variant="secondary" className="ml-2">
                {filteredQuotes.length} of {quotes.length}
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Create and manage multiple quotes for this project
            </p>
          </div>
          <Dialog open={isCreating} onOpenChange={setIsCreating}>
            <DialogTrigger asChild={true}>
              <Button
                size="default"
                onClick={() => navigate(`/quote-builder/${projectId}`)}
                className="w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Quote
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Quote</DialogTitle>
                <DialogDescription>
                  Create a new quote using the current project configuration.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor={quoteNameFieldId}>Quote Name *</Label>
                  <Input
                    id={quoteNameFieldId}
                    value={newQuoteName}
                    onChange={(e) => setNewQuoteName(e.target.value)}
                    placeholder="e.g., Standard Quote, Premium Option"
                  />
                </div>

                <div>
                  <Label htmlFor={quoteNotesFieldId}>Notes (Optional)</Label>
                  <Textarea
                    id={quoteNotesFieldId}
                    value={newQuoteNotes}
                    onChange={(e) => setNewQuoteNotes(e.target.value)}
                    placeholder="Additional notes for this quote..."
                    rows={3}
                  />
                </div>

                <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded">
                  <strong>Note:</strong> This will create a new quote using all services and items
                  currently configured in your project. You can edit the quote independently without
                  affecting the original project or other quotes.
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCreating(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={createQuoteFromProject}
                    disabled={!newQuoteName.trim() || isCreatingQuote}
                  >
                    {isCreatingQuote ? "Creating..." : "Create Quote"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filtering Controls */}
        {quotes.length > 0 && (
          <div className="mb-6 p-4 bg-muted/30 rounded-lg border">
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search quotes by reference number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
              </div>
              <div className="w-full lg:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="viewed">Viewed</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {quotes.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="mx-auto w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Quotes Yet</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Create your first quote from the current project configuration.
            </p>
            <Button
              onClick={() => navigate(`/quote-builder/${projectId}`)}
              size="lg"
              className="w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Quote
            </Button>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="mx-auto w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-4">
              <Filter className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Quotes Match Your Filters</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Try adjusting your search term or status filter to find quotes.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
              className="w-full sm:w-auto"
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredQuotes.map((quote) => (
              <Card
                key={quote.id}
                className="group relative overflow-hidden border-2 hover:shadow-lg hover:border-primary/30 transition-all duration-200"
              >
                {/* Status indicator bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600" />

                <CardContent className="p-5">
                  {/* Header Section */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-base text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {quote.quote_number}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{format(new Date(quote.created_at), "MMM dd, yyyy")}</span>
                      </div>
                      {quote.scheduled_date && (
                        <div className="flex items-center gap-2 text-xs text-emerald-600 mt-1">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>
                            Scheduled: {format(new Date(quote.scheduled_date), "MMM dd, yyyy")}
                          </span>
                        </div>
                      )}
                    </div>
                    <StatusSelector
                      currentStatus={quote.status}
                      scheduledDate={quote.scheduled_date}
                      onUpdate={(status, scheduledDate) =>
                        updateQuoteStatus(quote.id, status, scheduledDate)
                      }
                      trigger={
                        <div className="cursor-pointer ml-2" onClick={(e) => e.stopPropagation()}>
                          <StatusBadge status={quote.status} />
                        </div>
                      }
                    />
                  </div>

                  {/* Price Display */}
                  <div className="bg-muted/30 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Total Amount</span>
                      <span className="text-xl font-bold text-primary">
                        ${quote.total_amount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                      <span>Quote Progress</span>
                      <span className="font-medium">{quote.progress_percentage ?? 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                        style={{ width: `${quote.progress_percentage ?? 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {quote.completed_checklists ?? 0} of {quote.total_checklists ?? 0} checklists
                      completed
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t space-y-2">
                    {/* First Row: Edit and Delete */}
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 h-9"
                        onClick={(event) => {
                          event.stopPropagation();
                          openQuoteForEditing(quote);
                        }}
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteClick(quote.id, quote.quote_name);
                        }}
                        className="h-9 px-3"
                        title="Delete quote"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Second Row: View and Generate */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9"
                        onClick={(event) => {
                          event.stopPropagation();
                          openQuoteForViewing(quote);
                        }}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" />
                        View
                      </Button>
                      {onGenerateQuote && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 h-9"
                          onClick={(event) => {
                            event.stopPropagation();
                            onGenerateQuote(quote);
                          }}
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" />
                          Generate
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quote</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{quoteToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setQuoteToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Quote
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
